// HALTPrompt — Hungry/Angry/Lonely/Tired toggle row rendered inline at
// the top of the dose sheet. The previous "underlined link → expand
// card" pattern under-signaled importance (a footnote-shaped link) and
// then over-shouted on tap (a full card dominated the sheet). The new
// pattern is a single 36px-tall chip row that's always visible —
// importance carried by surface (warm copper-soft ground) instead of
// size — with a contextual self-help line that appears only when the
// user toggles a chip on.
//
// On the first toggle, the parent persists the HALT state via
// POST /api/halt. The dose POST endpoint later updates
// `taken_dose_after = 1` if a dose lands within 30 minutes.

import { useState } from 'react';
import { Button } from '../ui/Button';
import { haptics } from '../../lib/haptics';

export type HaltState = { hungry: 0 | 1; angry: 0 | 1; lonely: 0 | 1; tired: 0 | 1 };
type HaltKey = keyof HaltState;

interface HALTPromptProps {
  onChange: (state: HaltState) => void;
  onDefer?: () => void;
}

const LABELS: Record<HaltKey, string> = {
  hungry: 'Hungry',
  angry: 'Angry',
  lonely: 'Lonely',
  tired: 'Tired',
};

const SUGGESTIONS: Record<HaltKey, string> = {
  hungry: 'Try eating something — even 200 calories changes the chemistry. The craving will be quieter on the other side of a meal.',
  angry: 'Take 90 seconds for a body scan. Anger usually wants action; a dose pretends to be that action without solving it.',
  lonely: 'Text one person. Even a 3-word check-in. Connection is what the substance is mimicking.',
  tired: "Tired brains are bad at decisions. If you can, defer this 15 minutes and reassess after water + a stretch.",
};

const KEYS: readonly HaltKey[] = ['hungry', 'angry', 'lonely', 'tired'];

export function HALTPrompt({ onChange, onDefer }: HALTPromptProps) {
  const [state, setState] = useState<HaltState>({ hungry: 0, angry: 0, lonely: 0, tired: 0 });

  function toggle(key: HaltKey) {
    const next: HaltState = { ...state, [key]: state[key] ? 0 : 1 };
    setState(next);
    onChange(next);
    haptics.confirm();
  }

  const onKeys = KEYS.filter((k) => state[k] === 1);
  const anyOn = onKeys.length > 0;

  return (
    <div>
      <div className="halt-chip-row" role="group" aria-label="HALT check-in">
        <span className="halt-chip-row-label">HALT?</span>
        {KEYS.map((k) => (
          <button
            key={k}
            type="button"
            className="halt-chip"
            data-on={state[k] === 1}
            aria-pressed={state[k] === 1}
            onClick={() => toggle(k)}
          >
            {LABELS[k]}
          </button>
        ))}
      </div>
      {anyOn ? (
        <div className="halt-suggestion" role="status">
          {onKeys.map((k) => (
            <p key={k} className="halt-suggestion-line">
              <strong>{LABELS[k]}:</strong> {SUGGESTIONS[k]}
            </p>
          ))}
          {onDefer ? (
            <Button
              variant="secondary"
              size="md"
              onClick={onDefer}
              style={{ marginBlockStart: 'var(--space-2)' }}
            >
              Defer 15 min
            </Button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
